import { useState, useEffect, useCallback, useRef } from 'react'

import Select from 'react-select'
import Cookie from 'js-cookie'
import { Howl } from 'howler'

import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import { grey } from '@material-ui/core/colors'

import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Switch from '@material-ui/core/Switch'
import CircularProgress from '@material-ui/core/CircularProgress'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'

import { copyObject, isValidBarcodeCharacter } from '~/tools/utils'
import { post } from '~/tools/request'
import { useAppSelector } from '~/store'

import OneByOneProcessing from './OneByOneProcessing'
import AllAtOnceProcessing from './AllAtOnceProcessing'
import FulfillmentComplete from './FulfillmentComplete'
import { getStockAssignmentByItemIndex, extractSaleLineItemInfo } from './utils'

// Constants moved to their own exports
export const ORDER_DISPLAY_MODES = {
    ONE_BY_ONE: 'one_by_one',
    ALL_AT_ONCE: 'all_at_once',
}

export const ORDER_DISPLAY_OPTIONS = [
    { label: gettext('One by one'), value: ORDER_DISPLAY_MODES.ONE_BY_ONE },
    { label: gettext('All at once'), value: ORDER_DISPLAY_MODES.ALL_AT_ONCE },
]

export const PAGES = {
    LOADING: 'loading',
    FULFILLMENT_COMPLETE: 'fulfillment_complete',
    PROCESS_ORDER: 'process_order',
    ALL_FULFILLMENTS_COMPLETE: 'all_fulfillments_complete',
}

// Styles using makeStyles
const useStyles = makeStyles((theme) => ({
    dialog: {
        height: 'calc(100% - 75px)',
    },
    loadingDialogContent: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid lightgray',
    },
    allAtOnceError: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 20,
    },
    allAtOnceErrorIcon: {},
    allAtOnceErrorText: {
        marginLeft: 3,
        fontSize: '1rem !important',
        color: theme.palette.error.main,
    },
    titleSpacer: {
        flexGrow: 1,
    },
    startWithBarcodeText: {
        color: grey[500],
    },
    startWithBarcodeSwitch: {
        marginRight: 10,
    },
    showOrdersText: {
        color: grey[500],
        marginRight: '10px !important',
    },
    modeSelect: {
        width: 150,
        fontSize: '0.9rem !important',
        fontWeight: 'normal',
    },
    allConfirmed: {
        display: 'flex',
        alignItems: 'center',
    },
    allConfirmedIcon: {
        fontSize: 60,
        marginRight: 15,
    },
    allConfirmedIconText: {
        color: grey[600],
    },
    dialogActions: {
        borderTop: '1px solid lightgray',
        paddingRight: '30px !important',
        paddingTop: '15px !important',
        paddingBottom: '15px !important',
    },
    soundSwitchContainer: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 10,
    },
    soundSwitchText: {
        color: grey[600],
    },
    spacer: {
        flex: '1 1 auto',
    },
    counterText: {
        color: grey[600],
        marginRight: '10px !important',
    },
    actionButton: {},
    nextButton: {
        color: 'white',
    },
}))

const OrderProcessor = (props) => {
    const theme = useTheme()
    const classes = useStyles()

    const { open, handleClose, pickPackBatch } = props
    const { language, staticRoot } = useAppSelector((state) => state.initial)
    const {
        pickPackBatchFulfillmentsAPIURL,
        salesFromPKsAPIURL,
        fulfillmentAPIURL,
        fulfillmentDocumentsURL,
        assignSerialNumberApiURL,
        assignSerialNumberToBatchApiURL,
    } = useAppSelector((state) => state.initial.endpoints)

    // Initialize state with useState hooks
    const orderDisplayMode =
        localStorage.getItem('orderDisplayMode') || ORDER_DISPLAY_MODES.ONE_BY_ONE
    const orderDisplayOption = ORDER_DISPLAY_OPTIONS.find(
        (option) => option.value === orderDisplayMode
    )
    const showItemsWithSerialNumber =
        localStorage.getItem('showItemsWithSerialNumber') === 'true' ? true : false
    const getShippingLabel = localStorage.getItem('getShippingLabel') === 'true' ? true : false
    const getPackList = localStorage.getItem('getPackList') === 'true' ? true : false
    const getTaxInvoice = localStorage.getItem('getTaxInvoice') === 'true' ? true : false

    const [page, setPage] = useState(PAGES.LOADING)
    const [fulfillments, setFulfillments] = useState([])
    const [salesByID, setSalesByID] = useState(null)
    const [next, setNext] = useState(null)
    const [currentFulfillmentIndex, setCurrentFulfillmentIndex] = useState(0)
    const [currentFulfillmentLineItemIndex, setCurrentFulfillmentLineItemIndex] = useState(0)
    const [currentItemUnitIndex, setCurrentItemUnitIndex] = useState(0)
    const [oneByOneError, setOneByOneError] = useState(null)
    const [allAtOnceError, setAllAtOnceError] = useState(null)
    const [soundOn, setSoundOn] = useState(true)
    const [orderDisplayOptionState, setOrderDisplayOption] = useState(orderDisplayOption)
    const [showItemsWithSerialNumberState, setShowItemsWithSerialNumberState] =
        useState(showItemsWithSerialNumber)
    const [scannedFulfillmentLineItemID, setScannedFulfillmentLineItemID] = useState('')
    const [getShippingLabelState, setGetShippingLabel] = useState(getShippingLabel)
    const [getPackListState, setGetPackList] = useState(getPackList)
    const [getTaxInvoiceState, setGetTaxInvoice] = useState(getTaxInvoice)

    const updateFulfillmentLineItem = (fulfillment, fulfillmentLineItem) => {
        const newFulfillments = fulfillments.map((f) => {
            if (f.id !== fulfillment.id) return f

            f.fulfillment_line_items = f.fulfillment_line_items.map((fli) => {
                return fli.id === fulfillmentLineItem.id ? { ...fli } : fli
            })

            return { ...f }
        })
        setFulfillments(newFulfillments)
    }

    const updateStockAssignment = (fulfillment, fulfillmentLineItem, stockAssignment) => {
        const newFulfillments = fulfillments.map((f) => {
            if (f.id !== fulfillment.id) return f

            f.fulfillment_line_items = f.fulfillment_line_items.map((fli) => {
                if (fli.id !== fulfillmentLineItem.id) return fli

                fli.stock_assignments = fli.stock_assignments.map((sa) => {
                    if (sa.id === stockAssignment.id) return { ...stockAssignment }
                    return sa
                })
                return { ...fli }
            })

            return { ...f }
        })
        setFulfillments(newFulfillments)
    }

    // Refs
    const bufferRef = useRef('')
    const scanSuccessSoundRef = useRef(null)
    const scanErrorSoundRef = useRef(null)

    // Initialize sounds
    useEffect(() => {
        scanSuccessSoundRef.current = new Howl({
            src: staticRoot + 'web/audio/Scanner - success.mp3',
        })
        scanErrorSoundRef.current = new Howl({ src: staticRoot + 'web/audio/Scanner - error.mp3' })
    }, [staticRoot])

    // Fetch data when pickPackBatch changes
    useEffect(() => {
        if (pickPackBatch) {
            fetchFulfillmentsData(true)
        }
    }, [pickPackBatch, showItemsWithSerialNumberState])

    // Fetch fulfillments data
    const fetchFulfillmentsData = useCallback(
        (reset) => {
            let url

            if (next) {
                url = next
            } else {
                url = pickPackBatchFulfillmentsAPIURL.replace(
                    'pick_pack_batch_pk',
                    pickPackBatch.id
                )
            }

            return fetch(url).then((response) => {
                if (response.ok) {
                    response.json().then((data) => {
                        const newFulfillments = reset ? [] : copyObject(fulfillments)
                        let results = []
                        if (showItemsWithSerialNumberState) {
                            // Filter fulfillments with items that require serial number scanning.
                            // Ignore those without serial number tracking or with serial numbers already assigned.
                            data.results.forEach((f) => {
                                const flis = f.fulfillment_line_items.filter((fli) => {
                                    if (
                                        !fli.stock_record?.stock_item?.stock_item_warehouse_config
                                            ?.track_serial_number
                                    ) {
                                        return false
                                    }

                                    const assignedCount = fli.stock_assignments.filter(
                                        (sa) => sa.physical_item
                                    ).length
                                    return assignedCount < fli.quantity
                                })
                                if (flis.length > 0) {
                                    results.push({ ...f, fulfillment_line_items: flis })
                                }
                            })
                        } else {
                            results = data.results
                        }

                        results.forEach((fulfillment) => {
                            fulfillment.fulfillment_line_items.forEach((fli) => {
                                fli.numPacked = fli?.stock_record?.stock_item
                                    ?.stock_item_warehouse_config?.track_serial_number
                                    ? fli.stock_assignments.filter((sa) => !!sa.physical_item)
                                          .length
                                    : 0
                                fli.confirmed = false
                            })
                        })

                        const combinedFulfillments = newFulfillments.concat(results)

                        let activeFulfillmentIndex = 0
                        let activeFulfillmentLineItemIndex = 0
                        let activeItemUnitIndex = 0

                        combinedFulfillments.forEach((fulfillment, fulfillmentIndex) => {
                            fulfillment.fulfillment_line_items.forEach((fli, fliIndex) => {
                                fli.stock_assignments.forEach((sa) => {
                                    if (sa.physical_item) {
                                        activeItemUnitIndex += sa.quantity
                                        activeFulfillmentIndex = fulfillmentIndex
                                        activeFulfillmentLineItemIndex = fliIndex
                                    }
                                })
                            })
                        })

                        setNext(data.next)
                        setFulfillments(combinedFulfillments)

                        setCurrentFulfillmentIndex(activeFulfillmentIndex)
                        setCurrentFulfillmentLineItemIndex(activeFulfillmentLineItemIndex)
                        setCurrentItemUnitIndex(activeItemUnitIndex)

                        setPage(PAGES.PROCESS_ORDER)

                        const saleIDs = combinedFulfillments.map((fulfillment) => fulfillment.sale)
                        fetchSalesData(saleIDs)
                    })
                }
            })
        },
        [
            fulfillments,
            next,
            pickPackBatch,
            pickPackBatchFulfillmentsAPIURL,
            showItemsWithSerialNumberState,
        ]
    )

    // Fetch sales data
    const fetchSalesData = useCallback(
        (saleIDs) => {
            const queryString = saleIDs.map((saleID) => `pk=${saleID}`).join('&')
            const url = `${salesFromPKsAPIURL}?${queryString}`

            return fetch(url).then((response) => {
                if (response.ok) {
                    response.json().then((data) => {
                        const newSalesByID = {}

                        data.results.forEach((sale) => {
                            newSalesByID[sale.id] = sale
                        })

                        setSalesByID(newSalesByID)
                    })
                }
            })
        },
        [salesFromPKsAPIURL]
    )

    // Make fulfillment packed
    const makeFulfillmentPacked = useCallback(
        (fulfillmentID) => {
            const data = { status: 'waiting_for_3pl' }

            const newFulfillments = copyObject(fulfillments)
            const fulfillment = newFulfillments.find((f) => f.id === fulfillmentID)
            fulfillment.status = 'waiting_for_3pl'

            setFulfillments(newFulfillments)

            fetch(fulfillmentAPIURL.replace('fulfillment_pk', fulfillmentID), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'X-CSRFToken': Cookie.get('csrftoken'),
                },
                body: JSON.stringify(data),
            }).then((response) => {
                if (!response.ok) {
                    console.error('Error making fulfillment packed')
                }
            })
        },
        [fulfillments, fulfillmentAPIURL]
    )

    // Process next item in One by One mode
    const nextOneByOne = useCallback(() => {
        const newFulfillments = copyObject(fulfillments)
        const currentFulfillment = newFulfillments[currentFulfillmentIndex]
        const currentItem =
            currentFulfillment.fulfillment_line_items[currentFulfillmentLineItemIndex]

        currentItem.numPacked += 1

        if (currentItem.quantity - currentItem.numPacked === 0) {
            currentItem.confirmed = true

            const allItemsInFulfillmentPacked = currentFulfillment.fulfillment_line_items.every(
                (fli) => fli.confirmed
            )

            if (allItemsInFulfillmentPacked) {
                currentFulfillment.confirmed = true
                makeFulfillmentPacked(currentFulfillment.id)
            }

            setCurrentItemUnitIndex(0)
            setFulfillments(newFulfillments)
            setOneByOneError(false)

            setTimeout(() => {
                if (allItemsInFulfillmentPacked) {
                    setPage(PAGES.FULFILLMENT_COMPLETE)
                } else {
                    setCurrentFulfillmentLineItemIndex(currentFulfillmentLineItemIndex + 1)
                }
            }, 1000)
        } else if (currentItem.quantity - currentItem.numPacked > 0) {
            setCurrentItemUnitIndex(currentItemUnitIndex + 1)
            setFulfillments(newFulfillments)
            setOneByOneError(false)
        }
    }, [
        currentFulfillmentIndex,
        currentFulfillmentLineItemIndex,
        currentItemUnitIndex,
        fulfillments,
        makeFulfillmentPacked,
    ])

    // Process next item in All at Once mode
    const nextAllAtOnce = useCallback(
        (
            scannedValue,
            selectedFulfillment,
            selectedFulfillmentLineItem,
            selectedStockAssignment
        ) => {
            const newFulfillments = copyObject(fulfillments)
            const currentFulfillment = newFulfillments.find((f) => f.id === selectedFulfillment.id)
            const currentFulfillmentLineItem = currentFulfillment.fulfillment_line_items.find(
                (fli) => fli.id === selectedFulfillmentLineItem.id
            )
            if (selectedStockAssignment) {
                const currentStockAssignment = currentFulfillmentLineItem.stock_assignments?.find(
                    (sa) => sa.id === selectedStockAssignment.id
                )
                currentStockAssignment.assignSerialNumber = scannedValue
            }

            currentFulfillmentLineItem.numPacked += 1

            if (currentFulfillmentLineItem.quantity - currentFulfillmentLineItem.numPacked === 0) {
                currentFulfillmentLineItem.confirmed = true

                const allItemsInFulfillmentPacked = currentFulfillment.fulfillment_line_items.every(
                    (fli) => fli.confirmed
                )

                if (allItemsInFulfillmentPacked) {
                    currentFulfillment.confirmed = true
                    makeFulfillmentPacked(currentFulfillment.id)
                }

                setFulfillments(newFulfillments)
                setAllAtOnceError(false)
                setScannedFulfillmentLineItemID(currentFulfillmentLineItem.id)

                setTimeout(() => {
                    if (allItemsInFulfillmentPacked) {
                        setPage(PAGES.FULFILLMENT_COMPLETE)
                    }
                }, 1000)
            } else if (
                currentFulfillmentLineItem.quantity - currentFulfillmentLineItem.numPacked >
                0
            ) {
                setFulfillments(newFulfillments)
                setAllAtOnceError(false)
                setScannedFulfillmentLineItemID(currentFulfillmentLineItem.id)
            }
        },
        [fulfillments, makeFulfillmentPacked]
    )

    // Handle order display mode change
    const handleOrderDisplayModeChange = useCallback((value) => {
        setOrderDisplayOption(value)
        localStorage.setItem('orderDisplayMode', value.value)
    }, [])

    // Handle start with barcode change
    const handleShowItemsWithSerialNumberChange = useCallback((event) => {
        const newValue = event.target.checked
        localStorage.setItem('showItemsWithSerialNumber', newValue ? 'true' : 'false')
        setShowItemsWithSerialNumberState(newValue)
    }, [])

    // Handle next order click
    const handleNextOrderClick = useCallback(
        (event) => {
            event.currentTarget.blur()
            setCurrentFulfillmentIndex(currentFulfillmentIndex + 1)
            setCurrentFulfillmentLineItemIndex(0)
            setOneByOneError(false)
        },
        [currentFulfillmentIndex]
    )

    // Handle confirm order click
    const handleConfirmOrderClick = useCallback(
        (event) => {
            event.currentTarget.blur()

            const newFulfillments = copyObject(fulfillments)
            const currentFulfillment = newFulfillments[currentFulfillmentIndex]

            let nextOrderWaitTime

            currentFulfillment.fulfillment_line_items.forEach((fli) => {
                fli.numPacked = fli.quantity
                fli.confirmed = true
            })

            if (currentFulfillment.confirmed) {
                nextOrderWaitTime = 0
            } else {
                currentFulfillment.confirmed = true
                nextOrderWaitTime = 1000
                makeFulfillmentPacked(currentFulfillment.id)
            }

            setFulfillments(newFulfillments)
            setOneByOneError(false)

            setTimeout(() => {
                setPage(PAGES.FULFILLMENT_COMPLETE)
            }, nextOrderWaitTime)
        },
        [currentFulfillmentIndex, fulfillments, makeFulfillmentPacked]
    )

    // Handle previous order click
    const handlePreviousOrderClick = useCallback(
        (event) => {
            event.currentTarget.blur()
            setCurrentFulfillmentIndex(currentFulfillmentIndex - 1)
            setOneByOneError(false)
        },
        [currentFulfillmentIndex]
    )

    // Validate scans for different modes
    const isValidOneByOneScan = useCallback(
        async (scannedValue) => {
            const currentFulfillment = fulfillments[currentFulfillmentIndex]
            const currentItem =
                currentFulfillment.fulfillment_line_items[currentFulfillmentLineItemIndex]
            const stockItem = currentItem.stock_record?.stock_item || {}
            const isValidBarcode = scannedValue === stockItem.barcode
            if (isValidBarcode) {
                currentItem.checkBarcodeError = false
                updateFulfillmentLineItem(currentFulfillment, currentItem)
                return true
            }
            if (!stockItem.stock_item_warehouse_config?.track_serial_number) {
                currentItem.checkBarcodeError = true
                updateFulfillmentLineItem(currentFulfillment, currentItem)
                return false
            }

            // call api to check serial number
            const stockAssignment = getStockAssignmentByItemIndex(currentItem, currentItemUnitIndex)
            if (stockAssignment) {
                const result = await post(
                    assignSerialNumberApiURL.replace('fulfillment_pk', currentFulfillment.id),
                    {
                        fulfillment_line_item_id: currentItem.id,
                        stock_assignment_id: stockAssignment.id,
                        serial_number: scannedValue,
                    }
                )
                stockAssignment.assignSerialNumber = scannedValue
                stockAssignment.assignSerialNumberResult = result
                updateStockAssignment(currentFulfillment, currentItem, stockAssignment)
                setOneByOneError(result.error_message)
                return result.success
            }
            return false
        },
        [
            currentFulfillmentIndex,
            currentFulfillmentLineItemIndex,
            currentItemUnitIndex,
            fulfillments,
            assignSerialNumberApiURL,
        ]
    )

    const isValidAllAtOnceScan = useCallback(
        async (scannedValue) => {
            // check barcode first
            let isValidBarcode = false
            const selectedFulfillment = fulfillments[currentFulfillmentIndex]
            let selectedFulfillmentLineItem = null
            let selectedStockAssignment = null

            // biome-ignore lint/style/useForOf: <explanation>
            for (let j = 0; j < selectedFulfillment.fulfillment_line_items.length; j++) {
                const fli = selectedFulfillment.fulfillment_line_items[j]
                if (fli.confirmed) continue
                if (fli.stock_record && fli.stock_record?.stock_item?.barcode === scannedValue) {
                    isValidBarcode = true
                    selectedFulfillmentLineItem = fli
                    break
                }
            }

            if (isValidBarcode) {
                return [
                    true,
                    selectedFulfillment,
                    selectedFulfillmentLineItem,
                    selectedStockAssignment,
                ]
            }

            if (
                !selectedFulfillment.fulfillment_line_items.some(
                    (fli) =>
                        fli.stock_record?.stock_item?.stock_item_warehouse_config
                            ?.track_serial_number
                )
            ) {
                // none of the SKUs in this batch require a serial number
                setAllAtOnceError(gettext('That item was not found in this order'))
                return [
                    false,
                    selectedFulfillment,
                    selectedFulfillmentLineItem,
                    selectedStockAssignment,
                ]
            }

            // check serial number
            const result = await post(
                assignSerialNumberToBatchApiURL.replace('pick_pack_batch_pk', pickPackBatch.id),
                {
                    fulfillment_pk: selectedFulfillment.id,
                    serial_number: scannedValue,
                }
            )

            setAllAtOnceError(result.error_message)

            if (result.success) {
                // biome-ignore lint/style/useForOf: <explanation>
                for (let j = 0; j < selectedFulfillment.fulfillment_line_items.length; j++) {
                    const stockAssignment = selectedFulfillment.fulfillment_line_items[
                        j
                    ].stock_assignments.find((sa) => sa.id === result.stock_assignment_pk)
                    if (stockAssignment) {
                        selectedFulfillmentLineItem = selectedFulfillment.fulfillment_line_items[j]
                        selectedStockAssignment = stockAssignment
                        selectedStockAssignment.assignSerialNumber = scannedValue

                        return [
                            true,
                            selectedFulfillment,
                            selectedFulfillmentLineItem,
                            selectedStockAssignment,
                        ]
                    }
                }
            }

            return [
                false,
                selectedFulfillment,
                selectedFulfillmentLineItem,
                selectedStockAssignment,
            ]
        },
        [currentFulfillmentIndex, fulfillments]
    )

    // Handle barcode scans
    const scannedBarcodeOneByOne = useCallback(async () => {
        const currentFulfillment = fulfillments[currentFulfillmentIndex]
        const currentItem =
            currentFulfillment.fulfillment_line_items[currentFulfillmentLineItemIndex]

        if (currentItem.confirmed) {
            scanErrorSoundRef.current.play()
            bufferRef.current = ''
            return
        }

        const isValid = await isValidOneByOneScan(bufferRef.current)
        if (isValid) {
            if (soundOn) {
                scanSuccessSoundRef.current.play()
            }

            nextOneByOne()
        } else {
            if (soundOn) {
                scanErrorSoundRef.current.play()
            }
        }
    }, [
        currentFulfillmentIndex,
        currentFulfillmentLineItemIndex,
        fulfillments,
        isValidOneByOneScan,
        nextOneByOne,
        soundOn,
    ])

    const scannedBarcodeAllAtOnce = useCallback(async () => {
        const scannedValue = bufferRef.current
        const [isValid, selectedFulfillment, selectedFulfillmentLineItem, selectedStockAssignment] =
            await isValidAllAtOnceScan(scannedValue)

        if (isValid) {
            if (soundOn) {
                scanSuccessSoundRef.current.play()
            }

            nextAllAtOnce(
                scannedValue,
                selectedFulfillment,
                selectedFulfillmentLineItem,
                selectedStockAssignment
            )
        } else {
            if (soundOn) {
                scanErrorSoundRef.current.play()
            }
        }
    }, [isValidAllAtOnceScan, nextAllAtOnce, soundOn])

    // Keyboard event listener
    const keyDownListener = useCallback(
        (event) => {
            if (isValidBarcodeCharacter(event.key)) {
                bufferRef.current += event.key
            }

            if (event.key === 'Enter') {
                if (page === PAGES.PROCESS_ORDER) {
                    if (orderDisplayOptionState.value === ORDER_DISPLAY_MODES.ONE_BY_ONE) {
                        scannedBarcodeOneByOne()
                    } else if (orderDisplayOptionState.value === ORDER_DISPLAY_MODES.ALL_AT_ONCE) {
                        scannedBarcodeAllAtOnce()
                    }
                }

                bufferRef.current = ''
            }
        },
        [page, orderDisplayOptionState.value, scannedBarcodeOneByOne, scannedBarcodeAllAtOnce]
    )

    // Add and remove event listeners
    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', keyDownListener)
            return () => {
                document.removeEventListener('keydown', keyDownListener)
                bufferRef.current = ''

                if (page === PAGES.ALL_FULFILLMENTS_COMPLETE) {
                    const resetFulfillments = copyObject(fulfillments)
                    resetFulfillments.forEach((fulfillment) => {
                        fulfillment.confirmed = false
                        fulfillment.fulfillment_line_items.forEach((fli) => {
                            fli.confirmed = false
                            fli.numPacked = 0
                        })
                    })

                    setFulfillments(resetFulfillments)
                    setCurrentFulfillmentIndex(0)
                    setCurrentFulfillmentLineItemIndex(0)
                }
            }
        }
    }, [open, keyDownListener, page, fulfillments])

    // Toggle sound
    const handleToggleSound = useCallback((event) => {
        setSoundOn(event.target.checked)
    }, [])

    // Handle next fulfillment
    const handleNextFulfillment = useCallback(
        (event) => {
            event.currentTarget.blur()
            const allSalesPacked = fulfillments.every((fulfillment) => fulfillment.confirmed)

            if (allSalesPacked) {
                setPage(PAGES.ALL_FULFILLMENTS_COMPLETE)
                setTimeout(() => {
                    handleClose()
                }, 3000)
            } else {
                let nextFulfillmentIndex = currentFulfillmentIndex + 1
                if (nextFulfillmentIndex >= fulfillments.length) {
                    // end of list reached, find the first order that is not packed
                    nextFulfillmentIndex = fulfillments.findIndex(
                        (fulfillment) => !fulfillment.confirmed
                    )
                }

                if (nextFulfillmentIndex >= fulfillments.length - 2 && next) {
                    fetchFulfillmentsData()
                }

                setCurrentFulfillmentIndex(nextFulfillmentIndex)
                setCurrentFulfillmentLineItemIndex(0)
                setPage(PAGES.PROCESS_ORDER)
            }
        },
        [currentFulfillmentIndex, fetchFulfillmentsData, fulfillments, handleClose, next]
    )

    // Handle item actions
    const handleNextItemClick = useCallback(
        (event) => {
            event.currentTarget.blur()
            setCurrentFulfillmentLineItemIndex(currentFulfillmentLineItemIndex + 1)
            setOneByOneError(false)
        },
        [currentFulfillmentLineItemIndex]
    )

    const handleConfirmItemClick = useCallback(
        (event) => {
            event.currentTarget.blur()

            const newFulfillments = copyObject(fulfillments)
            const currentFulfillment = newFulfillments[currentFulfillmentIndex]
            const currentItem =
                currentFulfillment.fulfillment_line_items[currentFulfillmentLineItemIndex]

            let nextItemWaitTime

            currentItem.numPacked = currentItem.quantity

            if (currentItem.confirmed) {
                nextItemWaitTime = 0
            } else {
                currentItem.confirmed = true
                nextItemWaitTime = 1000
            }

            const allItemsInFulfillmentPacked = currentFulfillment.fulfillment_line_items.every(
                (fli) => fli.confirmed
            )

            if (allItemsInFulfillmentPacked) {
                currentFulfillment.confirmed = true
                makeFulfillmentPacked(currentFulfillment.id)
            }

            setFulfillments(newFulfillments)
            setOneByOneError(false)

            setTimeout(() => {
                if (allItemsInFulfillmentPacked) {
                    setPage(PAGES.FULFILLMENT_COMPLETE)
                } else {
                    setCurrentFulfillmentLineItemIndex(currentFulfillmentLineItemIndex + 1)
                }
            }, nextItemWaitTime)
        },
        [
            currentFulfillmentIndex,
            currentFulfillmentLineItemIndex,
            fulfillments,
            makeFulfillmentPacked,
        ]
    )

    const handlePreviousItemClick = useCallback(
        (event) => {
            event.currentTarget.blur()
            setCurrentFulfillmentLineItemIndex(currentFulfillmentLineItemIndex - 1)
            setOneByOneError(false)
        },
        [currentFulfillmentLineItemIndex]
    )

    // Check if there are partially processed orders
    const hasPartiallyProcessedOrders = useCallback(() => {
        return fulfillments.some((fulfillment) => {
            return fulfillment.fulfillment_line_items.some(
                (fli) =>
                    (fli.numPacked > 0 && !fli.confirmed) ||
                    (fli.confirmed && !fulfillment.confirmed)
            )
        })
    }, [fulfillments])

    // Handle document option checkbox
    const handleCheckDocumentOption = useCallback(
        (e) => {
            const option = e.target.value

            if (option === 'getShippingLabel') {
                setGetShippingLabel(!getShippingLabelState)
                localStorage.setItem('getShippingLabel', (!getShippingLabelState).toString())
            } else if (option === 'getPackList') {
                setGetPackList(!getPackListState)
                localStorage.setItem('getPackList', (!getPackListState).toString())
            } else if (option === 'getTaxInvoice') {
                setGetTaxInvoice(!getTaxInvoiceState)
                localStorage.setItem('getTaxInvoice', (!getTaxInvoiceState).toString())
            }
        },
        [getShippingLabelState, getPackListState, getTaxInvoiceState]
    )

    const handleItemUnitSelect = useCallback(
        (_fulfillment, fulfillmentLineItemIndex, itemUnitIndex) => {
            setCurrentFulfillmentLineItemIndex(fulfillmentLineItemIndex)
            setCurrentItemUnitIndex(itemUnitIndex)
        },
        []
    )

    // Get documents URL
    const getDocumentsURL = useCallback(() => {
        const params = {
            should_get_shipping_label: getShippingLabelState,
            should_get_pack_list: getPackListState,
            should_get_tax_invoice: getTaxInvoiceState,
        }

        const queryString = Object.keys(params)
            .map((key) => key + '=' + params[key])
            .join('&')

        const fulfillment = fulfillments[currentFulfillmentIndex]

        return fulfillmentDocumentsURL.replace('fulfillment_pk', fulfillment.id) + '?' + queryString
    }, [
        currentFulfillmentIndex,
        fulfillmentDocumentsURL,
        fulfillments,
        getPackListState,
        getShippingLabelState,
        getTaxInvoiceState,
    ])

    // Set up variables for rendering
    let fulfillment, sale, fulfillmentLineItem, saleLineItem, sliInfo
    if (fulfillments.length > 0 && salesByID) {
        fulfillment =
            fulfillments[
                currentFulfillmentIndex < fulfillments.length ? currentFulfillmentIndex : 0
            ]
        sale = salesByID[fulfillment.sale]
        fulfillmentLineItem =
            fulfillment.fulfillment_line_items.length > 0
                ? fulfillment.fulfillment_line_items[currentFulfillmentLineItemIndex]
                : null
        saleLineItem = sale?.sale_line_items.find(
            (sli) => sli.id === fulfillmentLineItem?.sale_line_item
        )
        sliInfo = saleLineItem ? extractSaleLineItemInfo(saleLineItem, language) : null
    }

    // Props for child components
    const oneByOneProps = {
        fulfillment,
        fulfillmentLineItem,
        sliInfo,
        oneByOneError,
        currentFulfillmentLineItemIndex,
        currentItemUnitIndex,
        handleNextItemClick,
        handleConfirmItemClick,
        handlePreviousItemClick,
        handleItemUnitSelect,
    }

    const allAtOnceProps = {
        fulfillment,
        sale,
        language,
        scannedFulfillmentLineItemID,
        currentFulfillmentLineItemIndex,
        currentItemUnitIndex,
        handleItemUnitSelect,
    }

    const fulfillmentCompleteProps = {
        sale,
        fulfillment,
        handleNextFulfillment,
        getShippingLabelState,
        getPackListState,
        getTaxInvoiceState,
        handleCheckDocumentOption,
        getDocumentsURL,
    }

    return (
        <Dialog classes={{ paper: classes.dialog }} open={open} maxWidth="xl" fullWidth={true}>
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Typography variant="h6">
                    {sale ? interpolate(gettext('Order %s'), [sale.sale_id]) : gettext('Order')}
                </Typography>
                {allAtOnceError && (
                    <div className={classes.allAtOnceError}>
                        <WarningIcon className={classes.allAtOnceErrorIcon} color="error" />
                        <Typography className={classes.allAtOnceErrorText} variant="h5">
                            {allAtOnceError}
                        </Typography>
                    </div>
                )}
                <div className={classes.titleSpacer} />

                <Typography className={classes.startWithBarcodeText} variant="body2">
                    {gettext('Only show items that require a serial number')}
                </Typography>

                <Switch
                    color="secondary"
                    className={classes.startWithBarcodeSwitch}
                    checked={showItemsWithSerialNumberState}
                    onChange={handleShowItemsWithSerialNumberChange}
                />

                <Typography className={classes.showOrdersText} variant="body2">
                    {gettext('Show orders')}
                </Typography>
                <Select
                    className={classes.modeSelect}
                    isClearable={false}
                    options={ORDER_DISPLAY_OPTIONS}
                    value={orderDisplayOptionState}
                    onChange={handleOrderDisplayModeChange}
                    // isDisabled={hasPartiallyProcessedOrders()}
                    theme={(reactSelectTheme) => ({
                        ...reactSelectTheme,
                        colors: {
                            ...reactSelectTheme.colors,
                            primary: theme.palette.secondary.main,
                        },
                    })}
                />
            </DialogTitle>

            {page === PAGES.LOADING && (
                <DialogContent className={classes.loadingDialogContent}>
                    <CircularProgress variant="indeterminate" size={60} color="secondary" />
                </DialogContent>
            )}

            {page === PAGES.ALL_FULFILLMENTS_COMPLETE && (
                <DialogContent className={classes.loadingDialogContent}>
                    <div className={classes.allConfirmed}>
                        <CheckCircleIcon className={classes.allConfirmedIcon} color="primary" />
                        <Typography variant="h5" className={classes.allConfirmedIconText}>
                            {gettext('Packed all orders in pick list')}
                        </Typography>
                    </div>
                </DialogContent>
            )}

            {page === PAGES.FULFILLMENT_COMPLETE && (
                <FulfillmentComplete {...fulfillmentCompleteProps} />
            )}

            {page === PAGES.PROCESS_ORDER &&
                orderDisplayOptionState.value === ORDER_DISPLAY_MODES.ONE_BY_ONE && (
                    <OneByOneProcessing {...oneByOneProps} />
                )}

            {page === PAGES.PROCESS_ORDER &&
                orderDisplayOptionState.value === ORDER_DISPLAY_MODES.ALL_AT_ONCE && (
                    <AllAtOnceProcessing {...allAtOnceProps} />
                )}

            <DialogActions className={classes.dialogActions}>
                <Button
                    className={classes.actionButton}
                    color="secondary"
                    onClick={handleClose}
                    size="large"
                    disableFocusRipple
                    disableRipple
                >
                    {gettext('Close')}
                </Button>
                <div className={classes.soundSwitchContainer}>
                    <Switch checked={soundOn} onChange={handleToggleSound} color="primary" />
                    <Typography variant="subtitle1" className={classes.soundSwitchText}>
                        {gettext('Sound')}
                    </Typography>
                </div>
                <div className={classes.spacer} />
                <Typography variant="subtitle1" className={classes.counterText}>
                    {interpolate(gettext('%s of %s orders'), [
                        currentFulfillmentIndex + 1,
                        fulfillments.length,
                    ])}
                </Typography>
                <Button
                    className={classes.actionButton}
                    color="primary"
                    variant="outlined"
                    onClick={handlePreviousOrderClick}
                    disabled={
                        [
                            PAGES.LOADING,
                            PAGES.FULFILLMENT_COMPLETE,
                            PAGES.ALL_FULFILLMENTS_COMPLETE,
                        ].includes(page) || currentFulfillmentIndex === 0
                    }
                    size="large"
                    disableFocusRipple
                    disableRipple
                >
                    {gettext('Previous order')}
                </Button>
                {Boolean(fulfillment) && Boolean(fulfillment.confirmed) ? (
                    <Button
                        className={`${classes.actionButton} ${classes.nextButton}`}
                        color="primary"
                        variant="contained"
                        onClick={handleNextOrderClick}
                        disabled={[
                            PAGES.LOADING,
                            PAGES.FULFILLMENT_COMPLETE,
                            PAGES.ALL_FULFILLMENTS_COMPLETE,
                        ].includes(page)}
                        size="large"
                        disableFocusRipple
                        disableRipple
                    >
                        {gettext('Next order')}
                    </Button>
                ) : (
                    <Button
                        className={`${classes.actionButton} ${classes.nextButton}`}
                        color="primary"
                        variant="contained"
                        onClick={handleConfirmOrderClick}
                        disabled={[
                            PAGES.LOADING,
                            PAGES.FULFILLMENT_COMPLETE,
                            PAGES.ALL_FULFILLMENTS_COMPLETE,
                        ].includes(page)}
                        size="large"
                        disableFocusRipple
                        disableRipple
                    >
                        {gettext('Confirm order and continue')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default OrderProcessor
